import React, { MouseEventHandler, ReactNode, useCallback, useEffect, useState } from "react"
import styled, { css, keyframes } from "styled-components"
import { theme } from "../../../theme"
import { ButtonProps, ButtonText, Button, ButtonVariant, ButtonSize } from "../../atoms/button"
import { Add, ArrowBack, Close, EditOutlined } from "@mui/icons-material"
import { Heading1, TextLink } from "../../atoms/text"
import { Stepper as StepperComponent, StepperProps } from "../../molecules/stepper"
import { ProgressChip, ProgressChipProps } from "../../atoms/chip"
import { ClickAwayListener, IconButton } from "@mui/material"
import { AvatarGroup, AvatarGroupProps } from "../../molecules/AvatarGroup"
import { AvatarSize } from "../../atoms/avatar"
import { AnimateAppearance } from "./AnimateAppearance"
import { motion } from "framer-motion"
import { PageSearch, PageSearchProps } from "../../atoms/inputs"

export enum HeaderIconLeftButton {
  close = "close",
  arrowBack = "arrowBack",
}

export type HeaderProps = {
  leftButton?: {
    icon: HeaderIconLeftButton
    onClick?: MouseEventHandler<HTMLButtonElement>
    href?: string
  }
  title?: {
    normal?: string
    bold?: string
    grey?: string
  }
  status?: {
    progressChip: ProgressChipProps | React.ReactElement
    button?: { onClick?: MouseEventHandler<HTMLAnchorElement>; label: string }
    tooltip?: {
      title: string
      description: string
    }
  }
  stepper?: StepperProps
  avatarGroup?: AvatarGroupProps
  actionsButtons?: Array<ButtonProps>
  elevation?: boolean
  callToActionButton?: {
    label: string
    onClick: MouseEventHandler<HTMLButtonElement>
    onClickAway?: () => void
    pulsing?: boolean
    dataCy?: string
  }
  serviceBanner?: ReactNode
  sticky?: {
    scrollableTarget: string
  }
  encounter?: {
    date: string
    avatarGroup: AvatarGroupProps
    editOnClick: MouseEventHandler<HTMLButtonElement>
  }
  search?: {
    pageSearch: Pick<
      PageSearchProps,
      | "value"
      | "onChange"
      | "onClear"
      | "onClearSideEffects"
      | "label"
      | "placeholder"
      | "onSubmit"
      | "onFocus"
      | "dataCy"
    >
    onExpand?: (isExpanded: boolean) => void
  }
}

export const Header = (props: HeaderProps) => {
  const {
    leftButton,
    stepper,
    title,
    actionsButtons,
    elevation,
    callToActionButton,
    status,
    sticky,
    avatarGroup,
    serviceBanner,
    encounter,
    search,
  } = props

  const [scroll, setScroll] = useState(false)
  const [isSearchFieldFocused, setIsSearchFieldFocused] = useState(false)
  const [isSearchFieldExpanded, setIsSearchFieldExpanded] = useState(false)

  const handleNavigation = useCallback(
    (e: Event) => {
      const elem = e.currentTarget as HTMLElement
      const scrollY = elem.scrollTop
      setScroll(scrollY > 0)
    },
    [scroll]
  )

  useEffect(() => {
    if (!sticky) return

    const doc = document.querySelector(sticky.scrollableTarget)
    doc && doc.addEventListener("scroll", handleNavigation)

    return () => {
      doc && doc.removeEventListener("scroll", handleNavigation)
    }
  }, [handleNavigation])

  useEffect(() => {
    search?.onExpand && search.onExpand(isSearchFieldExpanded)
  }, [isSearchFieldExpanded])

  const iconLeftButton = (
    <>
      {leftButton?.icon === HeaderIconLeftButton.arrowBack && <ArrowBack />}
      {leftButton?.icon === HeaderIconLeftButton.close && <CustomClose />}
    </>
  )

  const handleFocusSearchField = (value: boolean) => {
    setIsSearchFieldFocused(value)
    if (value) {
      setIsSearchFieldExpanded(true)
    }
  }

  const actionButton = (
    <ButtonCreate
      variant={ButtonVariant.primary}
      rounded
      icon={<Add />}
      $pulsing={callToActionButton?.pulsing}
      onClick={callToActionButton?.onClick}
      data-cy={callToActionButton?.dataCy}
    >
      {callToActionButton?.label}
    </ButtonCreate>
  )

  const callToActionContainer = callToActionButton && (
    <PrimaryActionContainer>
      {callToActionButton?.onClickAway ? (
        <ClickAwayListener onClickAway={callToActionButton?.onClickAway}>{actionButton}</ClickAwayListener>
      ) : (
        actionButton
      )}
    </PrimaryActionContainer>
  )

  const TitleContainer = title && (
    <Title>
      {title.bold && <HeaderTitleBold>{title.bold + (title.normal ? " " : "")}</HeaderTitleBold>}
      {title.normal && <HeaderTitleNormal>{title.normal}</HeaderTitleNormal>}
      {title.grey && <HeaderTitleGrey>&nbsp;{title.grey}</HeaderTitleGrey>}
    </Title>
  )

  if (search)
    return (
      <Root $sticky={!!sticky}>
        <Container $elevation={elevation} $scroll={scroll}>
          <PageHeader $callToActionButton={!!callToActionButton && !isSearchFieldExpanded} $search>
            <LeftTitleSpan>
              <AnimateAppearance show={!isSearchFieldExpanded}>
                <TitleHeader>{TitleContainer}</TitleHeader>
              </AnimateAppearance>
            </LeftTitleSpan>
            <PageSearch
              {...search.pageSearch}
              isExpanded={isSearchFieldExpanded}
              onFocus={handleFocusSearchField}
              isFocused={isSearchFieldFocused}
              dynamicWidth
              onClearSideEffects={() => {
                setIsSearchFieldExpanded(false)
              }}
            />
          </PageHeader>
        </Container>
        <AnimateAppearance show={!isSearchFieldExpanded}>{callToActionContainer}</AnimateAppearance>
      </Root>
    )

  return (
    <Root $sticky={!!sticky}>
      <Container $elevation={elevation} $scroll={scroll}>
        <PageHeader $callToActionButton={!!callToActionButton} $gap>
          <LeftSide>
            {leftButton && (
              <LeftButtonSpan>
                <span>
                  <LeftButton
                    onClick={leftButton.onClick}
                    icon={iconLeftButton}
                    href={leftButton.href}
                    size={ButtonSize.medium}
                  />
                </span>
              </LeftButtonSpan>
            )}
            <LeftTitleSpan>
              <TitleHeader>
                {TitleContainer}
                {encounter && (
                  <>
                    <EncounterInfo>
                      <AvatarGroup size={AvatarSize.small} max={3} children={encounter.avatarGroup.children} />
                      <EncounterDate>{encounter.date}</EncounterDate>
                      <IconButton color="primary" onClick={encounter.editOnClick} size={"small"}>
                        <EditOutlined fontSize="small" />
                      </IconButton>
                    </EncounterInfo>
                  </>
                )}
              </TitleHeader>
            </LeftTitleSpan>
            {status && (
              <Status>
                {React.isValidElement(status.progressChip) ? (
                  status.progressChip
                ) : (
                  <ProgressChipIcon {...status.progressChip} />
                )}
                {status.button && (
                  <StatusButtonDelete onClick={status.button.onClick}>{status.button.label}</StatusButtonDelete>
                )}
              </Status>
            )}
          </LeftSide>

          <PageHeaderActions>
            {avatarGroup && <AvatarGroup {...avatarGroup} />}
            {avatarGroup && actionsButtons && actionsButtons.length > 0 && <AvatarSeparator />}
            {actionsButtons?.map((buttonProps, index) => (
              <Button key={index} {...buttonProps} />
            ))}
          </PageHeaderActions>
        </PageHeader>
        {stepper && <StepperComponent {...stepper} />}
      </Container>
      {serviceBanner}
      {callToActionContainer}
    </Root>
  )
}

const Root = motion(styled.div<{ $sticky?: boolean }>`
  ${(p) =>
    p.$sticky &&
    css`
      position: sticky;
      width: 100%;
      height: auto;
      top: 0;
      left: 0;
      z-index: ${theme.zIndex.high};
      padding-bottom: 3px;
      margin-bottom: ${theme.spacing.lx};
    `}
`)

const Container = styled.div<{ $elevation?: boolean; $scroll?: boolean }>`
  ${(p) =>
    ((p.$elevation && !p.$scroll) || p.$scroll) &&
    css`
      box-shadow: 0 0 3px ${theme.color.grey1};
    `}
  background-color: ${theme.color.white};
  z-index: ${theme.zIndex.high};
`

const PageHeader = styled.div<{ $callToActionButton?: boolean; $gap?: boolean; $search?: boolean }>`
  display: flex;
  flex-flow: row wrap;
  justify-content: start;
  align-items: center;
  padding: ${theme.spacing.lx};
  ${(p) =>
    p.$gap &&
    css`
      gap: ${theme.spacing.lx};
    `}

  ${(p) =>
    p.$callToActionButton &&
    css`
      padding-bottom: 43px;
    `}

  ${(p) =>
    p.$search &&
    css`
      justify-content: space-between;
    `}
`

const PageHeaderActions = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  flex-wrap: wrap;
  row-gap: ${theme.spacing.l};
  column-gap: ${theme.spacing.l};
`

const HeaderTitleBold = styled(Heading1)`
  white-space: pre;
`

const ProgressChipIcon = styled(ProgressChip)`
  background-color: ${theme.color.primary50};
  height: ${theme.iconSize.xl};
  gap: ${theme.spacing.s};

  &,
  .MuiChip-icon {
    color: ${theme.color.primary};
  }
`

const HeaderTitleNormal = styled(HeaderTitleBold)`
  font-weight: normal;
  font-family: ${theme.font.regular};
`

const HeaderTitleGrey = styled(HeaderTitleNormal)`
  color: ${theme.color.black4};
`

const StatusButtonDelete = TextLink

const LeftButton = styled(ButtonText)`
  margin-right: ${theme.spacing.m};
  align-self: start;
  min-height: ${theme.buttonSize.xs};
  height: ${theme.buttonSize.xs};
  width: ${theme.buttonSize.xs};
`

const CustomClose = styled(Close)`
  width: ${theme.iconSize.l};
  height: ${theme.iconSize.l};
  min-height: ${theme.iconSize.l};
`

const TitleHeader = styled.div`
  display: flex;
  flex-grow: 1;
  flex-flow: row wrap;
  flex-direction: column;
  min-height: ${theme.height.xl};
  align-items: flex-start;
`

const LeftButtonSpan = styled.span`
  display: flex;
  align-items: center;
  height: ${theme.buttonSize.m};
`

const LeftTitleSpan = styled.span`
  display: flex;
  align-items: center;
`

const LeftSide = styled.span`
  display: flex;
  align-items: start;
  flex-grow: 1;
  flex-wrap: wrap;
`

const Title = styled.span`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-right: ${theme.spacing.m};
  min-height: ${theme.height.xl};
`

const Status = styled.div`
  display: flex;
  align-items: center;
  align-self: stretch;
  justify-content: start;
  height: ${theme.height.xl};
  gap: ${theme.spacing.l};
`

const ButtonCreate = styled(Button)<{ $pulsing?: boolean }>`
  box-shadow: 0 0 0 0 rgba(90, 153, 212, 1);
  ${(p) =>
    p.$pulsing &&
    css`
      animation: ${pulsingButton} 1s infinite;
    `}
`

const pulsingButton = keyframes`
  80% {
    box-shadow: 0 0 0 20px rgba(90, 153, 212, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(90, 153, 212, 0);
  }
`

const PrimaryActionContainer = styled.div`
  margin: -20px ${theme.spacing.lx} 20px ${theme.spacing.lx};
  height: ${theme.height.xl};
`

const AvatarSeparator = styled.div`
  border-right: 1px solid ${theme.color.white1};
  height: ${theme.height.xl};
`

const EncounterInfo = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  column-gap: ${theme.spacing.l};
`

const EncounterDate = styled.span`
  line-height: ${theme.spacing.lx};
  color: ${theme.color.black4};
`
